import HBIcon from "@/components/HBIcon";
import { TrackerA } from "@/components/Tracker";
import { COLLAB_ROLES } from "@/constants/board";
import useAppStore from "@/context/store";
import { shieldPin } from "@/services";
import { getContentType } from "@/utils/getContentType";
import { useMemoizedFn } from "ahooks";
import { Dropdown } from "antd";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useLogin } from "../LoginModal";
import styles from "./index.module.less";

interface PurePinMoreProps {
  pin: IPin;
  onShield?: (pin: IPin) => void;
  onReport?: () => void;
  overideClassName?: string;
  onEdit?: (pin: IPin) => void;
  onDelete?: (pin: IPin) => void;
  /** 是否显示我的操作 */
  showMeOperation?: boolean;
  /** 是否是全屏 */
  isFull?: boolean;
  moduleName?: string;
  searchWord?: string;
}

export default function PurePinMore({
  pin,
  onShield,
  onReport,
  overideClassName,
  onEdit,
  onDelete,
  showMeOperation = false,
  isFull = false,
  moduleName,
  searchWord,
}: PurePinMoreProps) {
  const { login } = useLogin();
  const user = useAppStore(({ user }) => user);
  const contentType = useMemo(() => {
    return getContentType(pin);
  }, [pin]);

  const [shieldShow, setShieldShow] = useState(false);
  const onShieldFn = useMemoizedFn(async (e) => {
    e.stopPropagation();
    setShieldShow(true);
  });

  /** 屏蔽原因 */
  const onShieldReason = useCallback(
    async (reason) => {
      const result = await shieldPin({
        item_type: 1,
        item_value: pin?.file_id,
        reason,
      });

      if (!result.err) {
        // 在 pin 对象中添加屏蔽原因字段
        const pinWithShieldReason = {
          ...pin,
          shield_reason: reason,
        };
        onShield?.(pinWithShieldReason);
        setShieldShow(false);
      }
    },
    [pin, onShield]
  );

  const isMe = user?.user_id === pin?.user_id;

  const canEdit = useMemo(() => {
    return (
      isMe ||
      // 当前用户是协作成员，且画板有编辑权限
      (pin?.is_collab_member &&
        pin?.board?.extra?.collab === COLLAB_ROLES.EDITOR) ||
      // 当前用户是画板的管理员
      pin?.board?.user_id === user?.user_id
    );
  }, [pin?.board, user, isMe]);

  const moreMenu = useMemo(() => {
    if (!user) {
      return <div></div>;
    }
    return (
      <div className={styles.menu} data-content-type={contentType}>
        {!isMe && (
          <>
            <TrackerA
              className={styles.menuItem}
              onClick={onShieldFn}
              data-gd-click="button_click"
              data-button-name="屏蔽"
              data-report-type="采集"
              data-report-pin-id={pin?.pin_id}
              data-report-file-id={pin?.file_id}
              data-report-content-id={pin?.file_material?.material_id}
              data-material-id={pin?.file_material?.material_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-content-id={pin?.pin_id}
              data-search-word={searchWord}
              data-ext={JSON.stringify(pin?.ext)}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              data-location="1"
            >
              <span className={styles.text}>屏蔽采集</span>
            </TrackerA>
            <TrackerA
              className={styles.menuItem}
              onClick={onReport}
              data-gd-click="button_click"
              data-button-name="举报"
              data-report-type="采集"
              data-report-pin-id={pin?.pin_id}
              data-report-file-id={pin?.file_id}
              data-report-content-id={pin?.file_material?.material_id}
              data-material-id={pin?.file_material?.material_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-content-id={pin?.pin_id}
              data-search-word={searchWord}
              data-ext={JSON.stringify(pin?.ext)}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
              data-location="2"
            >
              <span className={styles.text}>举报采集</span>
            </TrackerA>
          </>
        )}
        {canEdit && showMeOperation && (
          <>
            <TrackerA
              className={styles.menuItem}
              onClick={() => onEdit?.(pin)}
              data-gd-click="button_click"
              data-button-name="编辑"
              data-content-id={pin?.pin_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-material-id={
                pin?.file_material?.material_id || pin?.extra?.material_id
              }
              data-search-word={searchWord}
              data-content-type={contentType}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
            >
              <span className={styles.text}>编辑</span>
            </TrackerA>
            <TrackerA
              className={styles.menuItem}
              onClick={() => onDelete?.(pin)}
              data-gd-click="button_click"
              data-button-name="删除采集"
              data-content-id={pin?.pin_id}
              data-board-id={pin?.board_id}
              data-file-id={pin?.file_id}
              data-material-id={
                pin?.file_material?.material_id || pin?.extra?.material_id
              }
              data-search-word={searchWord}
              data-content-type={contentType}
              {...(moduleName ? { "data-module-name": moduleName } : null)}
            >
              <span className={styles.text}>删除</span>
            </TrackerA>
          </>
        )}
      </div>
    );
  }, [
    user,
    contentType,
    onShieldFn,
    pin,
    onReport,
    isMe,
    showMeOperation,
    onEdit,
    onDelete,
    moduleName,
    searchWord,
  ]);

  const shieldMenu = useMemo(() => {
    if (!shieldShow) {
      return null;
    }

    // 不感兴趣,看到了太多次,图片模糊,不够美观,内容引人不适,其他
    const shieldReasonList = [
      "内容不感兴趣",
      "看到了太多次",
      "图片模糊",
      "不够美观",
      "内容引人不适",
    ];

    return (
      <div className={classNames(styles.menu, styles.shieldMenu)}>
        <div className={styles.desc}>
          请告诉我们原因，我们将为你优化推荐结果：
        </div>
        {shieldReasonList.map((item, index) => (
          <TrackerA
            key={item}
            className={styles.menuItem}
            onClick={(e) => {
              onShieldReason(item);
            }}
            data-gd-click="button_click"
            data-button-name="屏蔽原因"
            data-report-reason={item}
            data-content-id={pin?.pin_id}
            data-board-id={pin?.board_id}
            data-file-id={pin?.file_id}
            data-material-id={pin?.file_material?.material_id}
            data-unique-id={pin?.pin_id}
            data-location={index + 1}
            data-content-type={contentType}
            data-search-word={searchWord}
            data-ext={JSON.stringify(pin?.ext)}
            {...(moduleName ? { "data-module-name": moduleName } : null)}
          >
            <span className={styles.text}>{item}</span>
          </TrackerA>
        ))}
      </div>
    );
  }, [onShieldReason, pin, shieldShow, contentType, moduleName, searchWord]);

  const onClick = useCallback(() => {
    if (!user) {
      login({
        source_location: "采集卡片更多操作",
      });
    }
  }, [login, user]);

  return (
    <Dropdown
      overlay={shieldMenu || moreMenu}
      placement="bottomLeft"
      trigger={["click"]}
      onVisibleChange={(visible) => {
        if (!visible) {
          setShieldShow(false);
        }
      }}
    >
      <div
        className={classNames(overideClassName || styles.more)}
        title="更多操作"
        onClick={onClick}
        data-gd-click="button_click"
        data-button-name="更多操作"
        data-content-id={pin?.pin_id}
        data-board-id={pin?.board_id}
        data-file-id={pin?.file_id}
        data-content-type={contentType}
        data-search-word={searchWord}
        {...(moduleName ? { "data-module-name": moduleName } : null)}
      >
        <HBIcon className={styles.icon} type={isFull ? "hb_more" : "ic_more"} />
      </div>
    </Dropdown>
  );
}
